import styles from './MapTabs.module.scss';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { setIsolatedViewsOwnerId, setMenuSelectedTab } from 'client/store/mapSlice/mapSlice';
import { Item } from 'react-stately';
import Popover from 'client/components/aria/Popover/Popover';
import Tabs from 'client/pages/MapPage/components/MapMenu/MapTabs/Tabs/Tabs';
import LayerControls from 'client/pages/MapPage/components/MapMenu/LayerControls/LayerControls';
import Legend from 'client/pages/MapPage/components/Legend/Legend';
import AssetOwners from 'client/pages/MapPage/components/MapMenu/AssetOwners/AssetOwners';
import CoverageAssetOwners from 'client/pages/MapPage/components/MapMenu/CoverageAssetOwners/CoverageAssetOwners';
import EnhancedMeasures from 'client/pages/MapPage/components/MapMenu/EnhancedMeasures/EnhancedMeasures';
import ObservationsTab from 'client/pages/MapPage/components/MapMenu/ObservationsTab/ObservationsTab';
import HelpTab from 'client/pages/MapPage/components/MapMenu/HelpTab/HelpTab';
import Icon from '@mdi/react';
import {
    mdiBell,
    mdiDomain,
    mdiEyeCheck,
    mdiHelp,
    mdiHelpCircle,
    mdiLayers,
    mdiMapLegend,
    mdiMapMarkerAlert
} from '@mdi/js';
import { mapMenuTabKeys, mapModes } from 'client/utils/constants/appStates';
import IsolatedViews from '../IsolatedViews/IsolatedViews';
import CMSService from 'client/services/CMS/CMS.service';
import Markdown from 'client/components/aria/MarkDown/Markdown';
import { ReactComponent as ObservationIcon } from 'client/assets/imgs/observations.svg';
import { resetIsolatedViewsState, resetSelectedOwnerId } from 'client/store/isolatedViewsSlice/isolatedViewsSlice';
import { setSectorSpecificStyles } from 'client/store/layersSlice/layersSlice';
import { filterAssets } from 'client/store/assetsSlice/assetsSlice';

const { DEFAULT, LOADING, MODIFYING, AOI, ADDING_OBSERVATION } = mapModes;
const ANY = 'ANY';

const {
    NOTIFICATIONS,
    LAYERS,
    LEGEND,
    ENHANCED_MEASURES,
    ISOLATED_VIEWS,
    ASSET_OWNERS,
    COVERAGE_ASSET_OWNERS,
    OBSERVATIONS,
    HELP
} = mapMenuTabKeys;

const MapTabs = ({ selectedAssetOwners, filterKey }) => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const assetOwnersFromState = useSelector(state => state?.assetOwners?.data);
    const sensitiveAssets = useSelector(state => state?.assets?.sensitiveAssets);
    const mapConfig = useSelector(state => state?.mapConfig?.result);
    const { mode } = useSelector(state => state?.map);
    const isMapMenuOpen = useSelector(state => state?.map?.menuOpen);
    const selectedKey = useSelector(state => state?.map?.menuSelectedTab);
    const previousSelectedKey = useRef(selectedKey);

    // Track if this is the initial render to prevent state updates during render
    const isInitialRender = useRef(true);

    // Wrap setSelectedKey to prevent updates during initial render
    const setSelectedKey = useCallback(key => {
        if (isInitialRender.current) {
            // Defer initial state updates to prevent render-time conflicts
            requestAnimationFrame(() => {
                dispatch(setMenuSelectedTab(key));
            });
        } else {
            dispatch(setMenuSelectedTab(key));
        }
    }, [dispatch]);

    // Mark that initial render is complete
    useEffect(() => {
        isInitialRender.current = false;
    }, []);

    useEffect(() => {
        if (previousSelectedKey.current === ISOLATED_VIEWS && selectedKey !== ISOLATED_VIEWS) {
            dispatch(setIsolatedViewsOwnerId(null));
            dispatch(resetSelectedOwnerId());
            dispatch(setSectorSpecificStyles({ sector: null, enabled: false }));
            dispatch(resetIsolatedViewsState());
            dispatch(filterAssets());
        }

        previousSelectedKey.current = selectedKey;
    }, [dispatch, selectedKey]);

    const { assetsInAOIOwners, operationalZone, nationalOrRegional } = assetOwnersFromState || {};
    const enableAssetOwnersTab = (mode.includes(DEFAULT) && assetOwnersFromState && !filterKey) || ((filterKey && selectedAssetOwners));
    const [observationsDisclaimer, setObservationsDisclaimer] = useState();

    useEffect(() => {
        if (selectedKey === OBSERVATIONS && !observationsDisclaimer) {
            CMSService.getObservationsDisclaimer().then(content => {
                setObservationsDisclaimer(content.markdown);
            });
        }
    }, [selectedKey]);

    const tabs = [
        {
            'data-testid': 'mapTabs.coverageAssetOwners',
            key: COVERAGE_ASSET_OWNERS,
            hideUnlessFiltered: true, // This tab is only shown when the filterKey is set to COVERAGE_ASSET_OWNERS
            enabledForModes: [ANY],
            disabledForModes: [],
            title: <FormattedMessage id="networkCoveragePage.title" />,
            prefix: <Icon path={mdiDomain} size={1} aria-hidden={true} />,
            children: <CoverageAssetOwners />
        },
        {
            'data-testid': 'mapTabs.notifications',
            key: NOTIFICATIONS,
            hideUnlessFiltered: false,
            enabledForModes: [ANY],
            disabledForModes: [],
            title: <FormattedMessage id="mapTabs.notifications.title" />,
            prefix: <Icon path={mdiBell} size={1} aria-hidden={true} />,
            children: null
        },
        {
            'data-testid': 'mapTabs.layers',
            key: LAYERS,
            hideUnlessFiltered: false,
            enabledForModes: [ANY],
            disabledForModes: [],
            title: <FormattedMessage id="mapTabs.layers.title" />,
            prefix: <Icon path={mdiLayers} size={1} aria-hidden={true} />,
            children: <LayerControls />
        },
        {
            'data-testid': 'mapTabs.legend',
            key: LEGEND,
            hideUnlessFiltered: false,
            enabledForModes: [ANY],
            disabledForModes: [],
            title: <FormattedMessage id="mapTabs.legend.title" />,
            prefix: <Icon path={mdiMapLegend} size={1} aria-hidden={true} />,
            children: mapConfig ? <Legend /> : null
        },
        {
            'data-testid': 'mapTabs.enhancedMeasures',
            key: ENHANCED_MEASURES,
            hideUnlessFiltered: false,
            enabledForModes: [AOI],
            disabledForModes: [LOADING, MODIFYING, ADDING_OBSERVATION],
            title: <FormattedMessage id="mapTabs.enhancedMeasures.title" />,
            prefix: <Icon path={mdiMapMarkerAlert} size={1} aria-hidden={true} />,
            suffix: <Popover data-testid="mapTabs.enhancedMeasures.help" aria-label={intl.formatMessage({ id: 'general.help' })} content={
                <>
                    <p className="govuk-body-s govuk-!-font-size-14">
                        <FormattedMessage id="mapTabs.enhancedMeasures.popover.1" />
                    </p>
                    <p className="govuk-body-s govuk-!-font-size-14">
                        <FormattedMessage id="mapTabs.enhancedMeasures.popover.2" />
                    </p>
                    <p className="govuk-body-s govuk-!-font-size-14 govuk-!-margin-bottom-0">
                        <FormattedMessage id="mapTabs.enhancedMeasures.popover.3" />
                    </p>
                </>
            }>
                <Icon path={mdiHelpCircle} size={1.1} />
            </Popover>,
            children: sensitiveAssets?.length ? <EnhancedMeasures /> : null
        },
        {
            'data-testid': 'mapTabs.isolatedViews',
            key: ISOLATED_VIEWS,
            hideUnlessFiltered: false,
            enabledForModes: [AOI],
            disabledForModes: [LOADING, MODIFYING],
            title: <FormattedMessage id="mapTabs.isolatedViews.title" />,
            prefix: <Icon path={mdiEyeCheck} size={1} aria-hidden={true} />,
            suffix: <Popover data-testid="mapTabs.isolatedViews.help" aria-label={intl.formatMessage({ id: 'general.help' })} content={
                <p className="govuk-body-s govuk-!-font-size-14 govuk-!-margin-bottom-0">
                    <FormattedMessage id="mapTabs.isolatedViews.popover" />
                </p>
            }>
                <Icon path={mdiHelpCircle} size={1.1} />
            </Popover>,
            children: assetsInAOIOwners?.length ? <IsolatedViews /> : null
        },
        {
            'data-testid': 'mapTabs.assetOwners',
            key: ASSET_OWNERS,
            hideUnlessFiltered: false,
            enabledForModes: [DEFAULT, AOI],
            disabledForModes: [LOADING, MODIFYING],
            title: <FormattedMessage id="mapTabs.assetOwners.title" />,
            prefix: <Icon path={mdiDomain} size={1} aria-hidden={true} />,
            suffix: <Popover data-testid="mapTabs.assetOwners.help" aria-label={intl.formatMessage({ id: 'general.help' })} content={
                <>
                    <p className="govuk-body-s govuk-!-font-size-14">
                        <FormattedMessage id="mapTabs.assetOwners.popover.1" />
                    </p>
                    <p className="govuk-body-s govuk-!-font-size-14">
                        <FormattedMessage id="mapTabs.assetOwners.popover.2" />
                    </p>
                    <p className="govuk-body-s govuk-!-font-size-14 govuk-!-margin-bottom-0">
                        <FormattedMessage id="mapTabs.assetOwners.popover.3" />
                    </p>
                </>
            }>
                <Icon path={mdiHelpCircle} size={1.1} />
            </Popover>,
            children: enableAssetOwnersTab
                ? <AssetOwners
                    assetsInAOIOwners={assetsInAOIOwners}
                    operationalZone={operationalZone}
                    nationalOrRegional={nationalOrRegional} />
                : null
        },
        {
            'data-testid': 'mapTabs.observations',
            key: OBSERVATIONS,
            hideUnlessFiltered: false,
            enabledForModes: [AOI],
            disabledForModes: [LOADING, MODIFYING],
            title: <FormattedMessage id="mapTabs.observations.title" />,
            prefix: <ObservationIcon size={1} aria-hidden={true} />,
            suffix: <Popover data-testid="mapTabs.observations.help" aria-label={intl.formatMessage({ id: 'general.help' })} content={
                <Markdown>
                    {observationsDisclaimer || intl.formatMessage({ id: 'loading' })}
                </Markdown>
            }>
                <Icon path={mdiHelpCircle} size={1.1} />
            </Popover>,
            children: <ObservationsTab assetsInAOIOwners={assetsInAOIOwners} />
        },
        {
            'data-testid': 'mapTabs.help',
            key: HELP,
            hideUnlessFiltered: false,
            enabledForModes: [ANY],
            disabledForModes: [],
            title: <FormattedMessage id="mapTabs.help.title" />,
            prefix: <Icon path={mdiHelp} size={1} aria-hidden={true} />,
            children: <HelpTab />

        }
    ];

    let filteredTabs;
    if (filterKey) {
        filteredTabs = [{
            ...tabs.find(tab => tab.key === filterKey)
        }];
    } else {
        filteredTabs = tabs.filter(tab => {
            // Hide tab if it's hidden, unless it's the one being filtered for.
            return !tab.hideUnlessFiltered || filterKey === tab.key;
        });
    }

    const defaultSelectedKey = filterKey || LAYERS;

    const isTabEnabled = useCallback(tabKey => {
        const selectedTab = tabs.find(tab => tab.key === tabKey);

        if (!selectedTab?.children) {
            return false;
        }
        const isInAnEnabledMode = selectedTab.enabledForModes.some(enabledMode => {
            if (enabledMode === ANY) {
                return true;
            }
            return mode.includes(enabledMode);
        });
        const isInADisabledMode = selectedTab.disabledForModes.some(disabledMode => {
            if (disabledMode === ANY) {
                return true;
            }
            return mode.includes(disabledMode);
        });

        return (isInAnEnabledMode && !isInADisabledMode);
    }, [tabs, mode]);

    // Fix: Use requestAnimationFrame to defer state updates and prevent render-time conflicts
    useEffect(() => {
        if (!isTabEnabled(selectedKey)) {
            setSelectedKey(defaultSelectedKey);
        }
    }, [isTabEnabled, selectedKey, defaultSelectedKey, setSelectedKey]);

    const disabledKeys = tabs.filter(tab => !isTabEnabled(tab.key)).map(tab => tab.key);

    return (
        <div className={isMapMenuOpen ? styles.container : styles.closed} data-testid="mapTabs.container">
            <Tabs
                selectedKey={selectedKey}
                onSelectionChange={setSelectedKey}
                aria-label="Map Menu"
                orientation="vertical"
                disabledKeys={disabledKeys}
                data-testid="mapTabs"
            >
                {filteredTabs.map(tab => (
                    <Item {...tab} key={tab.key} />
                ))}
            </Tabs>
        </div>
    );
};

MapTabs.defaultProps = {
    filterKey: null,
    selectedAssetOwners: null
};

export default MapTabs;
